import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Check, ArrowRight, Sparkles, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('growth');
  const [includeCustomWebsite, setIncludeCustomWebsite] = useState(false);
  const [shopName, setShopName] = useState('');
  const [loading, setLoading] = useState(false);

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 49,
      visualizations: '400',
      description: 'Perfect for small shops',
      features: [
        '400 visualizations/month',
        'Embed on your website',
        'Basic analytics',
        'Email support'
      ]
    },
    {
      id: 'growth',
      name: 'Growth',
      price: 79,
      visualizations: '1,000',
      description: 'For growing businesses',
      popular: true,
      features: [
        '1,000 visualizations/month',
        'Embed on your website',
        'Advanced analytics',
        'Priority support'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 119,
      visualizations: 'Unlimited',
      description: 'For high-volume shops',
      features: [
        'Unlimited visualizations',
        'Embed on your website',
        'Full analytics',
        'Dedicated support',
        'Custom branding'
      ]
    }
  ];

  const calculateTotal = () => {
    const plan = plans.find(p => p.id === selectedPlan);
    let total = plan?.price || 0;
    if (includeCustomWebsite) total += 9;
    return total;
  };

  const handleCheckout = async () => {
    if (!user) {
      navigate('/signup');
      return;
    }

    if (!shopName.trim()) {
      toast.error('Please enter your shop name');
      return;
    }

    // Sanitize shop name for URL
    const sanitizedShopName = shopName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    if (!sanitizedShopName) {
      toast.error('Please enter a valid shop name');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/billing/checkout`, {
        origin_url: window.location.origin,
        plan: selectedPlan,
        include_custom_website: includeCustomWebsite,
        shop_name: sanitizedShopName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-bold text-2xl tracking-tight text-black flex items-center gap-2">
              WrapViz
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Link to="/app">
                  <Button variant="ghost">Dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost">Login</Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="bg-black text-white hover:bg-gray-800">Sign Up</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-black tracking-tight text-black mb-4"
          >
            Simple, transparent pricing
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-gray-600"
          >
            Choose the plan that fits your business. Cancel anytime.
          </motion.p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative bg-white rounded-2xl p-6 cursor-pointer transition-all ${
                  selectedPlan === plan.id 
                    ? 'ring-2 ring-black shadow-xl' 
                    : 'border border-gray-200 hover:border-gray-300'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-black text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-black mb-1">{plan.name}</h3>
                  <p className="text-sm text-gray-500">{plan.description}</p>
                </div>
                
                <div className="mb-6">
                  <span className="text-4xl font-black text-black">€{plan.price}</span>
                  <span className="text-gray-500">/month</span>
                </div>
                
                <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">
                    <Sparkles className="w-4 h-4 inline mr-2" />
                    {plan.visualizations} visualizations/month
                  </p>
                </div>
                
                <ul className="space-y-3">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {selectedPlan === plan.id && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-on Section */}
      <section className="pb-12 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            onClick={() => setIncludeCustomWebsite(!includeCustomWebsite)}
            className={`bg-white rounded-2xl p-6 cursor-pointer transition-all ${
              includeCustomWebsite 
                ? 'ring-2 ring-black shadow-lg' 
                : 'border border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  includeCustomWebsite ? 'bg-black' : 'bg-gray-100'
                }`}>
                  <Globe className={`w-6 h-6 ${includeCustomWebsite ? 'text-white' : 'text-gray-600'}`} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-black">Custom WrapViz Website</h3>
                  <p className="text-sm text-gray-500">
                    Standalone branded visualizer at viz.wrapviz.ai/your-shop
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className="text-2xl font-bold text-black">+€9</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  includeCustomWebsite ? 'bg-black border-black' : 'border-gray-300'
                }`}>
                  {includeCustomWebsite && <Check className="w-4 h-4 text-white" />}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Free embed option note */}
          <p className="text-center text-sm text-gray-500 mt-4">
            Or embed the visualizer on your existing website (Shopify, Wix, WordPress) for free with any plan
          </p>
        </div>
      </section>

      {/* Shop Name & Checkout */}
      <section className="pb-20 px-6">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gray-50 rounded-2xl p-6"
          >
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Shop Name
            </label>
            <input
              type="text"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder="e.g., Premium Wraps NYC"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black mb-4"
            />
            <p className="text-xs text-gray-500 mb-6">
              Your visualizer URL: <span className="font-mono">viz.wrapviz.ai/{shopName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'your-shop'}</span>
            </p>

            {/* Total */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <span className="font-medium text-gray-700">Total</span>
              <span className="text-2xl font-black text-black">€{calculateTotal()}/month</span>
            </div>

            <Button 
              onClick={handleCheckout}
              disabled={loading}
              className="w-full bg-black text-white hover:bg-gray-800 py-6 text-base font-medium"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {user ? 'Continue to Checkout' : 'Sign Up & Checkout'}
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>

            <p className="text-xs text-center text-gray-500 mt-4">
              Secure checkout powered by Stripe. Cancel anytime.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-12 text-black">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'What happens if I exceed my visualization limit?', a: 'The visualizer will be paused until your limit resets next month. You can upgrade anytime to get more visualizations.' },
              { q: 'Can I cancel anytime?', a: 'Yes! You can cancel your subscription at any time. Your access continues until the end of your billing period.' },
              { q: 'What\'s the difference between embed and custom website?', a: 'Embed lets you add the visualizer to your existing site. The custom website gives you a standalone page hosted by us at viz.wrapviz.ai/your-shop.' },
              { q: 'Do I need technical skills?', a: 'No! After checkout, you\'ll get ready-to-use code snippets. Just copy and paste.' }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="font-semibold text-black mb-2">{faq.q}</h3>
                <p className="text-gray-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-500">© 2025 WrapViz. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
